import { useEffect, useRef, useState } from "react";
import { useController } from "react-hook-form";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import clsx from "clsx";
import { setSaveFileIndex } from "../../../store/file/slice";
import getFileIcon from "../../../utils/get-file-icon";
import { FileAPI } from "../../../services/file";
import Toast from "../../../helpers/status-text";

import styles from "./styles.module.scss";

const MAX_FILE_SIZE_MB = 20;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

//? Separate file upload component

const UploadFile = ({
  url,
  name,
  error,
  index,
  control,
  className,
  disabled = false,
  multiple = false,
  title = "Ներբեռնել ֆայլեր",
  accept = ".pdf, .doc, .docx, .jpg, .msg",
}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const listFilePaths = useSelector(
    (state) => state.file.listFile.map((item) => item?.file?.path ?? null),
    shallowEqual
  );

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const BASE_URL = process.env.REACT_APP_BASE_URL_IMG;

  const {
    field: { name: fieldName, onChange, value },
  } = useController({
    name,
    control,
  });

  const handleFileChange = async (e) => {
    let progressInterval;
    try {
      const selectedFiles = Array.from(e.target.files ?? []);

      if (!selectedFiles.length) return;

      const oversizedFiles = selectedFiles.filter(
        (file) => file.size > MAX_FILE_SIZE
      );

      if (oversizedFiles.length) {
        Toast.error(
          `⚠️ Ֆայլը գերազանցում է ${MAX_FILE_SIZE_MB}ՄԲ առավելագույն չափի սահմանափակումը։`
        );
        return;
      }

      setIsUploading(true);

      const initialProgress = selectedFiles.map((file, index) => ({
        id: index,
        fileName: file.name,
        progress: 0,
        status: "uploading",
      }));
      setUploadProgress(initialProgress);

      onChange(selectedFiles);

      progressInterval = setInterval(() => {
        setUploadProgress((prev) =>
          prev.map((item) => ({
            ...item,
            progress: Math.min(item.progress + Math.random() * 30, 95),
          }))
        );
      }, 200);

      if (index !== undefined) {
        dispatch(setSaveFileIndex(index));
      }

      const uploadResponse = await dispatch(
        FileAPI.postUploadFiles({ file: selectedFiles, url })
      ).unwrap();

      clearInterval(progressInterval);
      setUploadProgress((prev) =>
        prev.map((item) => ({
          ...item,
          progress: 100,
          status: "completed",
        }))
      );

      const responseFiles = Array.isArray(uploadResponse?.data)
        ? uploadResponse.data
        : [];

      const newUploadedFiles = selectedFiles.map((file, fileIndex) => {
        const uploadedFileData = responseFiles[fileIndex];
        return {
          id: Date.now() + fileIndex,
          name: file.name,
          type: file.type,
          size: file.size,
          uploadedAt: new Date(),
          icon: getFileIcon(file.name),
          url: uploadedFileData?.path
            ? `${BASE_URL}${uploadedFileData.path}`
            : null,
        };
      });

      // If multiple is false, replace the existing files, otherwise append
      if (multiple) {
        setUploadedFiles((prev) => [...prev, ...newUploadedFiles]);
      } else {
        setUploadedFiles(newUploadedFiles);
      }

      // Clear progress after a short delay
      setTimeout(() => {
        setUploadProgress([]);
      }, 2000);
    } catch (err) {
      console.error("Upload error:", err);
      // Update progress to show error
      setUploadProgress((prev) =>
        prev.map((item) => ({
          ...item,
          status: "error",
          progress: 0,
        }))
      );
      Toast.error(
        err.message || "Ֆայլը ներբեռնելու ընթացքում սխալ տեղի ունեցավ։"
      );
    } finally {
      setIsUploading(false);
      // Ensure progress interval is always cleared (e.g., on error)
      try {
        if (typeof progressInterval !== "undefined" && progressInterval) {
          clearInterval(progressInterval);
        }
      } catch (_) {}
    }
  };

  useEffect(() => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      setUploadedFiles([]);
      setUploadProgress([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [value]);

  useEffect(() => {
    if (listFilePaths.length > 0 && uploadedFiles.length > 0) {
      setUploadedFiles((prev) => {
        const hasMissingUrls = prev.some((f) => !f.url);
        if (!hasMissingUrls) return prev;

        const updatedFiles = prev.map((file, index) => {
          const pathFromStore = listFilePaths[index];
          if (file.url || !pathFromStore) return file;
          return {
            ...file,
            url: `${BASE_URL}${pathFromStore}`,
          };
        });

        const didChange = updatedFiles.some((f, i) => f.url !== prev[i]?.url);
        if (!didChange) return prev;

        return multiple ? updatedFiles : updatedFiles.slice(-1);
      });
    }
  }, [listFilePaths, BASE_URL, multiple, uploadedFiles.length]);

  const renderUploadProgress = () => {
    if (uploadProgress.length === 0) return null;

    return (
      <div className={styles.upload_progress}>
        {uploadProgress.map((item) => (
          <div key={item.id} className={styles.progress_item}>
            <div className={styles.progress_header}>
              <span className={styles.file_name}>{item.fileName}</span>
              <span className={styles.status_badge}>
                {item.status === "uploading" && "Բեռնվում է..."}
                {item.status === "completed" && "✓ Ավարտված"}
                {item.status === "error" && "✗ Սխալ"}
              </span>
            </div>
            <div className={styles.progress_bar}>
              <div
                className={`${styles.progress_fill} ${
                  item.status === "completed" ? styles.completed : ""
                }`}
                style={{ width: `${item.progress}%` }}
              ></div>
            </div>
            <div className={styles.progress_percent}>
              {Math.round(item.progress)}%
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderUploadedContent = () => {
    if (uploadedFiles.length === 0) return null;

    return (
      <div className={styles.uploaded_section}>
        <h3 className={styles.section_title}>
          {multiple ? "Բեռնված ֆայլեր" : "Բեռնված ֆայլ"}
        </h3>
        <div>
          {uploadedFiles.map((file) => {
            const FileCard = ({ children }) => {
              if (file.url) {
                return (
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Բացել ${file.name} նոր պատուհանում`}
                    className={`${styles.file_card} ${styles.clickable}`}
                  >
                    {children}
                  </a>
                );
              }
              return <div className={styles.file_card}>{children}</div>;
            };

            return (
              <FileCard key={file.id}>
                <div className={styles.file_icon}>
                  <img src={file.icon} alt="file icon" />
                </div>
                <div className={styles.file_details}>
                  <div className={styles.file_name} title={file.name}>
                    {file.name}
                  </div>
                  <div className={styles.file_status}>
                    ✓ Հաջողությամբ բեռնված
                    {file.url && (
                      <span className={styles.click_hint}>
                        {" "}
                        • Սեղմեք բացելու համար
                      </span>
                    )}
                  </div>
                </div>
              </FileCard>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className={clsx(className, styles.upload_file)}>
        <div className={styles.dropzone}>
          <label className={disabled || isUploading ? styles.disabled : ""}>
            <img src="/icons/upload-file.svg" alt="upload" />
            <span>{title}</span>
            <input
              type="file"
              name={fieldName}
              ref={fileInputRef}
              disabled={disabled || isUploading}
              multiple={multiple}
              accept={accept}
              onChange={handleFileChange}
            />
          </label>
          {error && <p className={styles.error}>{error}</p>}
        </div>
      </div>

      {renderUploadProgress()}

      {renderUploadedContent()}

      {value?.path ? (
        <div className={styles.uploaded_section}>
          <h3 className={styles.section_title}>Բեռնված ֆայլ</h3>
          <div>
            <a
              href={`${BASE_URL}${value.path}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`${styles.file_card} ${styles.clickable}`}
              title={`Բացել ${value.path.split("/").pop()} նոր պատուհանում`}
            >
              <div className={styles.file_icon}>
                <img src={getFileIcon(value.path)} alt="file icon" />
              </div>
              <div className={styles.file_details}>
                <div
                  className={styles.file_name}
                  title={value.path.split("/").pop()}
                >
                  {value.path.split("/").pop()}
                </div>
                <div className={styles.file_status}>
                  ✓ Հաջողությամբ բեռնված
                  <span className={styles.click_hint}>
                    {" "}
                    • Սեղմեք բացելու համար
                  </span>
                </div>
              </div>
            </a>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default UploadFile;
